import React, { useState, useRef, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  TextField,
  FormLabel,
  FormHelperText,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Button,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import coursesTypeData from 'Constants/courseData';
import useApiCaller from 'hooks/apiCaller';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English' },
  { code: 'as', name: 'Assamese' },
  { code: 'bn', name: 'Bengali' },
  { code: 'hi', name: 'Hindi' },
  { code: 'kn', name: 'Kannada' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'te', name: 'Telugu' },
  { code: 'ta', name: 'Tamil' },
  { code: 'or', name: 'Oriya' },
  { code: 'mr', name: 'Marathi' },
  { code: 'gu', name: 'Gujarati' },
];

const UpdateCourse = () => {
  const fileInputRef = useRef();
  const apiCaller = useApiCaller();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const location = useLocation();
  const courseIdFromState = location.state?.courseId;
  const finalCourseId = courseId || courseIdFromState;

  const [courseDetails, setCourseDetails] = useState({
    courseTitle: '',
    description: '',
    startDate: null,
    endDate: null,
    courseType: '',
    isMandatory: '',
    totalNumOfAttempts: null,
    passingScore: '33',
  });
  const [uploadedCourses, setUpdatedCourses] = useState([]);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [courseUrl, setCourseUrl] = useState('');
  const [quesUrl, setQuestionUrl] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Multi-language video support
  const [availableVideos, setAvailableVideos] = useState([]);
  const [videoUpdates, setVideoUpdates] = useState({}); // { langCode: File }
  const [newLanguageVideos, setNewLanguageVideos] = useState([]);

  // Multi-language question support
  const [availableQuestionLanguages, setAvailableQuestionLanguages] = useState([]);
  const [questionUpdates, setQuestionUpdates] = useState({}); // { langCode: File }
  const [newLanguageQuestions, setNewLanguageQuestions] = useState([]);

  // Subtitle editing
  const [availableSubtitles, setAvailableSubtitles] = useState([]);
  const [selectedSubtitleLang, setSelectedSubtitleLang] = useState('en');
  const [subtitleContent, setSubtitleContent] = useState('');
  const [subtitleColor, setSubtitleColor] = useState('#ffffff');
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [subtitleLoading, setSubtitleLoading] = useState(false);

  const handleFileChange = (val) => {
    setUpdatedCourses([...uploadedCourses, val]);
  };

  const handleQuestionnaireUpload = (val) => {
    setQuestionnaire([...questionnaire, val]);
  };

  const handleOnChange = (key, value) => {
    setCourseDetails((prevCourseDetails) => ({
      ...prevCourseDetails,
      [key]: value,
    }));
  };

  const handleDeleteItem = (index) => {
    setUpdatedCourses(uploadedCourses.filter((_, idx) => idx !== index));
  };

  const handleDeleteQuestionnaire = (index) => {
    setQuestionnaire(questionnaire.filter((_, idx) => idx !== index));
  };

  // Video management functions
  const handleVideoReplace = (langCode, file) => {
    setVideoUpdates({ ...videoUpdates, [langCode]: file });
  };

  const handleAddNewLanguageVideo = () => {
    setNewLanguageVideos([...newLanguageVideos, { languageCode: '', file: null }]);
  };

  const handleRemoveNewLanguageVideo = (index) => {
    setNewLanguageVideos(newLanguageVideos.filter((_, i) => i !== index));
  };

  const handleNewLanguageVideoChange = (index, field, value) => {
    const updated = [...newLanguageVideos];
    updated[index][field] = value;
    setNewLanguageVideos(updated);
  };

  const getAvailableLanguagesForNew = (currentIndex) => {
    const existingCodes = availableVideos.map((v) => v.code);
    const selectedCodes = newLanguageVideos
      .map((lv, idx) => (idx !== currentIndex ? lv.languageCode : null))
      .filter(Boolean);
    return LANGUAGE_OPTIONS.filter(
      (lang) => !existingCodes.includes(lang.code) && !selectedCodes.includes(lang.code)
    );
  };

  // Question management functions
  const handleQuestionReplace = (langCode, file) => {
    setQuestionUpdates({ ...questionUpdates, [langCode]: file });
  };

  const handleAddNewLanguageQuestion = () => {
    setNewLanguageQuestions([...newLanguageQuestions, { languageCode: '', file: null }]);
  };

  const handleRemoveNewLanguageQuestion = (index) => {
    setNewLanguageQuestions(newLanguageQuestions.filter((_, i) => i !== index));
  };

  const handleNewLanguageQuestionChange = (index, field, value) => {
    const updated = [...newLanguageQuestions];
    updated[index][field] = value;
    setNewLanguageQuestions(updated);
  };

  const getAvailableLanguagesForNewQuestion = (currentIndex) => {
    const existingCodes = availableQuestionLanguages.map((q) => q.code);
    const selectedCodes = newLanguageQuestions
      .map((lq, idx) => (idx !== currentIndex ? lq.languageCode : null))
      .filter(Boolean);
    return LANGUAGE_OPTIONS.filter(
      (lang) => !existingCodes.includes(lang.code) && !selectedCodes.includes(lang.code)
    );
  };

  // Subtitle editing functions
  const handleLoadSubtitle = async () => {
    setSubtitleLoading(true);
    try {
      const response = await apiCaller.get(`/user/getSubtitleFile`, {
        params: { courseId: finalCourseId, lang: selectedSubtitleLang },
        responseType: 'blob',
      });
      const text = await response.data.text();
      setSubtitleContent(text);
      // Load the saved color for this subtitle language
      const match = availableSubtitles.find((s) => s.code === selectedSubtitleLang);
      setSubtitleColor(match?.color || '#ffffff');
      setIsEditingSubtitle(true);
      setErrors((prev) => ({ ...prev, subtitle: '' }));
    } catch (error) {
      console.error('Error loading subtitle:', error);
      setErrors((prev) => ({ ...prev, subtitle: 'Failed to load subtitle' }));
    } finally {
      setSubtitleLoading(false);
    }
  };

  const handleSaveSubtitle = async () => {
    // Validate WebVTT format
    if (!subtitleContent.trim().startsWith('WEBVTT')) {
      setErrors((prev) => ({
        ...prev,
        subtitle: 'Invalid WebVTT format. Content must start with "WEBVTT"',
      }));
      return;
    }

    setSubtitleLoading(true);
    try {
      await apiCaller.put('/super-user/updateSubtitle', {
        courseId: finalCourseId,
        languageCode: selectedSubtitleLang,
        content: subtitleContent,
        color: subtitleColor,
      });
      setIsEditingSubtitle(false);
      setSubtitleContent('');
      alert('Subtitle updated successfully!');
    } catch (error) {
      console.error('Error saving subtitle:', error);
      setErrors((prev) => ({ ...prev, subtitle: 'Failed to save subtitle' }));
    } finally {
      setSubtitleLoading(false);
    }
  };

  const handleCancelSubtitleEdit = () => {
    setIsEditingSubtitle(false);
    setSubtitleContent('');
    setErrors((prev) => ({ ...prev, subtitle: '' }));
  };

  const handleSumbitUploadDetails = async () => {
    setIsLoading(true);

    const formData = new FormData();

    const { courseUrl, quesUrl, ...otherCourseDetails } = courseDetails;

    Object.keys(otherCourseDetails).forEach((key) => {
      formData.append(key, otherCourseDetails[key]);
    });

    if (uploadedCourses.length > 0) {
      uploadedCourses.forEach((file) => {
        formData.append('courseUrl', file);
      });
    } else if (courseUrl) {
      formData.append('courseUrl', courseUrl);
    }

    if (questionnaire.length > 0) {
      questionnaire.forEach((file) => {
        formData.append('quesUrl', file);
      });
    } else if (quesUrl) {
      formData.append('quesUrl', quesUrl);
    }

    // Add video updates
    Object.keys(videoUpdates).forEach((langCode) => {
      if (videoUpdates[langCode]) {
        formData.append(`videoContent[${langCode}]`, videoUpdates[langCode]);
      }
    });

    // Add new language videos
    newLanguageVideos.forEach((lv) => {
      if (lv.file && lv.languageCode) {
        formData.append(`videoContent[${lv.languageCode}]`, lv.file);
      }
    });

    // Add question updates (existing language replacements)
    Object.keys(questionUpdates).forEach((langCode) => {
      if (questionUpdates[langCode]) {
        formData.append(`questionContent[${langCode}]`, questionUpdates[langCode]);
      }
    });

    // Add new language question files
    newLanguageQuestions.forEach((lq) => {
      if (lq.file && lq.languageCode) {
        formData.append(`questionContent[${lq.languageCode}]`, lq.file);
      }
    });

    try {
      const response = await apiCaller.put(`super-user/updateCourse/${finalCourseId}`, formData);
      if (response.data.status === 'success') {
        setIsLoading(false);
        navigate('/courses');
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        const response = await apiCaller.get(`super-user/getCourse?courseId=${finalCourseId}`);
        const courseData = response?.data?.data?.courseDetail;
        setCourseDetails({
          courseTitle: courseData.courseTitle,
          description: courseData.description,
          startDate: courseData.startDate,
          endDate: courseData.endDate,
          courseType: courseData.courseType,
          isMandatory: courseData.isMandatory,
          totalNumOfAttempts: courseData.maxAttempt,
          passingScore: courseData.passingScore,
        });
        setUpdatedCourses(courseData.uploadedCourses || []);
        setQuestionnaire(courseData.questionnaire || []);
        setCourseUrl(courseData.courseUrl || '');
        setQuestionUrl(courseData.quesUrl || '');

        // Set available videos, subtitles, and question languages
        setAvailableVideos(courseData.availableVideos || []);
        setAvailableSubtitles(courseData.availableSubtitles || []);
        setAvailableQuestionLanguages(courseData.availableQuestionLanguages || []);
      } catch (error) {
        console.log(error);
      }
    };

    if (finalCourseId) {
      fetchCourseDetails();
    }
  }, [finalCourseId]);

  return (
    <Layout>
      <Box
        sx={{
          fontSize: '24px',
          fontWeight: 500,
          background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
          position: 'relative',
        }}
        className="p-3"
      >
        Update Course
      </Box>
      <Box className="mx-auto" sx={{ width: '60%' }}>
        {/* Basic Course Details */}
        <Box className="mt-2">
          <TextField
            fullWidth
            label="Title of Courses"
            size="small"
            onChange={(e) => handleOnChange('courseTitle', e.target.value)}
            value={courseDetails.courseTitle}
            error={!!errors.courseTitle}
            helperText={errors.courseTitle}
          />
        </Box>
        <Box className="mt-2">
          <FormControl fullWidth>
            <FormLabel>Course description</FormLabel>
            <TextField
              placeholder="Text"
              multiline
              rows={2}
              fullWidth
              onChange={(e) => handleOnChange('description', e.target.value)}
              value={courseDetails.description}
            />
            <FormHelperText sx={{ marginLeft: 0 }}>{errors.description}</FormHelperText>
          </FormControl>
        </Box>

        <Grid container spacing={1} className="mt-1">
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel>Course Start Date</FormLabel>
              <TextField
                fullWidth
                size="small"
                type="date"
                onChange={(e) => handleOnChange('startDate', e.target.value)}
                value={courseDetails.startDate}
                error={!!errors.startDate}
                helperText={errors.startDate}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel>Course End Date</FormLabel>
              <TextField
                fullWidth
                size="small"
                type="date"
                onChange={(e) => handleOnChange('endDate', e.target.value)}
                value={courseDetails.endDate}
                error={!!errors.endDate}
                helperText={errors.endDate}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={1} className="mt-1">
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel>Course Type</FormLabel>
              <Select
                size="small"
                value={courseDetails.courseType}
                onChange={(e) => handleOnChange('courseType', e.target.value)}
              >
                {coursesTypeData.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.courseType}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel>Is the Course Compulsory</FormLabel>
              <Select
                size="small"
                value={courseDetails.isMandatory}
                onChange={(e) => handleOnChange('isMandatory', e.target.value)}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
              <FormHelperText>{errors.isMandatory}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={1} className="mt-1">
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel>Number of Attempts</FormLabel>
              <Select
                size="small"
                value={courseDetails.totalNumOfAttempts}
                onChange={(e) => handleOnChange('totalNumOfAttempts', e.target.value)}
              >
                {[1, 2, 3, 4, 5].map((attempt) => (
                  <MenuItem key={attempt} value={attempt}>
                    {attempt}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.totalNumOfAttempts}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel>Passing Score</FormLabel>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={courseDetails.passingScore}
                onChange={(e) => handleOnChange('passingScore', e.target.value)}
                error={!!errors.passingScore}
                helperText={errors.passingScore}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ marginY: 3 }} />

        {/* Video Content Management (for all video courses) */}
        {courseDetails.courseType === 'video' && (
          <>
            <Box className="mt-3">
              <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
                Video Content Management
              </Typography>
              <Card sx={{ backgroundColor: '#F5FFF1' }}>
                <CardContent>
                  {availableVideos.length > 0 && (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
                        Current Videos:
                      </Typography>
                      {availableVideos.map((video) => (
                        <Box
                          key={video.code}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 1,
                            marginBottom: 1,
                            backgroundColor: '#fff',
                            borderRadius: 1,
                          }}
                        >
                          <Typography>
                            {video.isDefault && '✓ '}
                            {video.name}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            sx={{ textTransform: 'none' }}
                          >
                            Replace Video
                            <input
                              type="file"
                              hidden
                              accept="video/*"
                              onChange={(e) => handleVideoReplace(video.code, e.target.files[0])}
                            />
                          </Button>
                        </Box>
                      ))}
                    </>
                  )}

                  {availableVideos.length === 0 && (
                    <Alert severity="info" sx={{ marginBottom: 2 }}>
                      This course uses the legacy video system. Add language-specific videos below
                      to enable multi-language support.
                    </Alert>
                  )}

                  {/* New language videos */}
                  {newLanguageVideos.map((lv, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        padding: 1,
                        marginBottom: 1,
                        backgroundColor: '#fff',
                        borderRadius: 1,
                      }}
                    >
                      <Select
                        size="small"
                        value={lv.languageCode}
                        onChange={(e) =>
                          handleNewLanguageVideoChange(index, 'languageCode', e.target.value)
                        }
                        displayEmpty
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="" disabled>
                          Select Language
                        </MenuItem>
                        {getAvailableLanguagesForNew(index).map((lang) => (
                          <MenuItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        variant="outlined"
                        size="small"
                        component="label"
                        sx={{ textTransform: 'none', flex: 1 }}
                      >
                        {lv.file ? lv.file.name : 'Upload Video'}
                        <input
                          type="file"
                          hidden
                          accept="video/*"
                          onChange={(e) =>
                            handleNewLanguageVideoChange(index, 'file', e.target.files[0])
                          }
                        />
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveNewLanguageVideo(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddNewLanguageVideo}
                    sx={{ marginTop: 1, background: '#2800B1' }}
                  >
                    Add New Language Video
                  </Button>
                </CardContent>
              </Card>
            </Box>

            <Divider sx={{ marginY: 3 }} />
          </>
        )}

        {/* Subtitle Editing Section (for video courses) */}
        {courseDetails.courseType === 'video' && (
          <>
            <Box className="mt-3">
              <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
                Subtitle Editing
              </Typography>
              <Card sx={{ backgroundColor: '#F5FFF1' }}>
                <CardContent>
                  {availableSubtitles.length === 0 ? (
                    <Alert severity="warning">
                      No subtitles available for this course. Subtitles are auto-generated when you
                      upload a video with an English subtitle file.
                    </Alert>
                  ) : (
                    <>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <FormLabel>Select Language</FormLabel>
                            <Select
                              size="small"
                              value={selectedSubtitleLang}
                              onChange={(e) => setSelectedSubtitleLang(e.target.value)}
                              disabled={isEditingSubtitle}
                            >
                              {availableSubtitles.map((sub) => (
                                <MenuItem key={sub.code} value={sub.code}>
                                  {sub.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          {!isEditingSubtitle ? (
                            <Button
                              variant="contained"
                              startIcon={<EditIcon />}
                              onClick={handleLoadSubtitle}
                              disabled={subtitleLoading}
                              sx={{ background: '#2800B1' }}
                            >
                              {subtitleLoading ? (
                                <CircularProgress size={20} />
                              ) : (
                                'Load & Edit Subtitle'
                              )}
                            </Button>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                onClick={handleSaveSubtitle}
                                disabled={subtitleLoading}
                                sx={{ background: '#2800B1' }}
                              >
                                {subtitleLoading ? <CircularProgress size={20} /> : 'Save Subtitle'}
                              </Button>
                              <Button variant="outlined" onClick={handleCancelSubtitleEdit}>
                                Cancel
                              </Button>
                            </Box>
                          )}
                        </Grid>
                      </Grid>

                      {isEditingSubtitle && (
                        <Box className="mt-3">
                          <FormLabel>Subtitle Content (WebVTT Format)</FormLabel>
                          <TextField
                            fullWidth
                            multiline
                            rows={15}
                            value={subtitleContent}
                            onChange={(e) => setSubtitleContent(e.target.value)}
                            sx={{
                              marginTop: 1,
                              fontFamily: 'monospace',
                              '& textarea': {
                                fontFamily: 'monospace',
                                fontSize: '12px',
                              },
                            }}
                          />
                          {errors.subtitle && (
                            <FormHelperText error>{errors.subtitle}</FormHelperText>
                          )}

                          {/* Subtitle Color Picker */}
                          <Box sx={{ mt: 2 }}>
                            <FormLabel>Subtitle Color</FormLabel>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                              <input
                                type="color"
                                value={subtitleColor}
                                onChange={(e) => setSubtitleColor(e.target.value)}
                                style={{
                                  width: 48,
                                  height: 40,
                                  border: '1px solid #ccc',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  padding: 2,
                                }}
                              />
                              <Typography variant="body2" sx={{ color: '#475569' }}>
                                Choose the color for subtitle text
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                mt: 1,
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                                background: '#000',
                                display: 'inline-block',
                              }}
                            >
                              <Typography
                                sx={{ color: subtitleColor, fontSize: 13, fontFamily: 'monospace' }}
                              >
                                Preview subtitle text
                              </Typography>
                            </Box>
                          </Box>

                          <Alert severity="info" sx={{ marginTop: 1 }}>
                            Manual edits will NOT trigger auto-translation. Each language must be
                            edited independently.
                          </Alert>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>

            <Divider sx={{ marginY: 3 }} />
          </>
        )}

        {/* Question Content Management */}
        <Box className="mt-3">
          <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
            Question Content Management
          </Typography>
          <Card sx={{ backgroundColor: '#F5FFF1' }}>
            <CardContent>
              {availableQuestionLanguages.length > 0 && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
                    Current Question Languages:
                  </Typography>
                  {availableQuestionLanguages.map((ql) => (
                    <Box
                      key={ql.code}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 1,
                        marginBottom: 1,
                        backgroundColor: '#fff',
                        borderRadius: 1,
                      }}
                    >
                      <Typography>
                        {ql.isDefault && '✓ '}
                        {ql.name}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        component="label"
                        sx={{ textTransform: 'none' }}
                      >
                        Replace Question File
                        <input
                          type="file"
                          hidden
                          accept=".xlsx,.xls"
                          onChange={(e) => handleQuestionReplace(ql.code, e.target.files[0])}
                        />
                      </Button>
                    </Box>
                  ))}
                </>
              )}

              {availableQuestionLanguages.length === 0 && (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                  This course uses a single question file. Add language-specific question files
                  below to enable multi-language support.
                </Alert>
              )}

              {/* New language question files */}
              {newLanguageQuestions.map((lq, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    padding: 1,
                    marginBottom: 1,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                  }}
                >
                  <Select
                    size="small"
                    value={lq.languageCode}
                    onChange={(e) =>
                      handleNewLanguageQuestionChange(index, 'languageCode', e.target.value)
                    }
                    displayEmpty
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="" disabled>
                      Select Language
                    </MenuItem>
                    {getAvailableLanguagesForNewQuestion(index).map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                    sx={{ textTransform: 'none', flex: 1 }}
                  >
                    {lq.file ? lq.file.name : 'Upload Question File (.xlsx)'}
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls"
                      onChange={(e) =>
                        handleNewLanguageQuestionChange(index, 'file', e.target.files[0])
                      }
                    />
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveNewLanguageQuestion(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddNewLanguageQuestion}
                sx={{ marginTop: 1, background: '#2800B1' }}
              >
                Add New Language Question
              </Button>
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ marginY: 3 }} />

        {/* Legacy File Upload Section (for non-video or legacy video courses) */}
        <Box className="mt-2">
          <FormControl fullWidth>
            <FormLabel>Upload Courses</FormLabel>
            <TextField
              fullWidth
              size="small"
              type="file"
              inputRef={fileInputRef}
              onChange={(e) => handleFileChange(e.target.files[0])}
            />
            <Box className="mt-1">
              {uploadedCourses.map((file, index) => (
                <Box key={index}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteItem(index)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
            </Box>
            {courseUrl && (
              <Box className="mt-1" style={{ display: 'flex' }}>
                <Typography>Existing Course URL:</Typography>
                <a style={{ marginLeft: '15px' }} href={courseUrl} download>
                  Download Course
                </a>
              </Box>
            )}
          </FormControl>
        </Box>

        <Box className="mt-2">
          <FormControl fullWidth>
            <FormLabel>Upload Questionnaire</FormLabel>
            <TextField
              fullWidth
              size="small"
              type="file"
              inputRef={fileInputRef}
              onChange={(e) => handleQuestionnaireUpload(e.target.files[0])}
            />
            <Box className="mt-1">
              {questionnaire.map((file, index) => (
                <Box key={index}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteQuestionnaire(index)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
            </Box>
            {quesUrl && (
              <Box className="mt-1" style={{ display: 'flex' }}>
                <Typography>Existing Questionnaire URL:</Typography>
                <a style={{ marginLeft: '15px' }} href={quesUrl} download>
                  Download Questionnaire
                </a>
              </Box>
            )}
          </FormControl>
        </Box>

        <Box className="text-center mt-2 mb-4">
          <Button
            variant="contained"
            color="primary"
            disabled={isLoading}
            onClick={handleSumbitUploadDetails}
            sx={{ background: '#2800B1' }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default UpdateCourse;
